import { useMemo } from 'react';

import { useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';
import { useServerEvents } from '#lib/useServerEvents';

import {
	searchCompaniesByName,
	fetchCompanyDetail,
	fetchCompanyFinancialHistory,
	fetchCompanyEvents,
	fetchCompanyContacts,
	fetchCompanyDirectors,
} from '../../api/companyDetailApi';
import { fetchLeadsBySearchQueryId } from '../../api/leadApi';

import { companiesKeys } from './keys';

export const companiesByProspectQueryIdOptions = (
	prospectQueryId,
	{ page = 1, pageSize = 100, orderBy = 'created-at', orderDir = 'desc', filters = {} } = {},
) =>
	queryOptions({
		queryKey: companiesKeys.byProspectQuery(prospectQueryId, { page, pageSize, orderBy, orderDir, filters }),
		queryFn: async () => {
			const result = await fetchLeadsBySearchQueryId(prospectQueryId, {
				page,
				pageSize,
				orderBy,
				orderDir,
				filters,
			});
			if (result.error) throw result.error;
			return result;
		},
		staleTime: STALE_TIMES.STANDARD,
	});

export function useCompaniesByProspectQueryIdWithRealtime(
	prospectQueryId,
	{ page = 1, pageSize = 100, orderBy = 'created-at', orderDir = 'desc', filters = {} } = {},
	isJobRunning = false,
) {
	const queryClient = useQueryClient();
	const query = useQuery({
		...companiesByProspectQueryIdOptions(prospectQueryId, { page, pageSize, orderBy, orderDir, filters }),
		enabled: Boolean(prospectQueryId),
	});

	const topics = useMemo(
		() => (prospectQueryId && isJobRunning ? [`leads:${prospectQueryId}`] : []),
		[prospectQueryId, isJobRunning],
	);

	useServerEvents(topics, {
		enabled: Boolean(prospectQueryId) && isJobRunning,
		onEvent: () => {
			// Prefix invalidation: during a scrape every page/sort/filter view of this
			// query is stale, and it cannot drift from the exact key shape above.
			queryClient.invalidateQueries({ queryKey: companiesKeys.byProspectQueryBase(prospectQueryId) });
		},
	});

	return query;
}

export function useCompanyNameSearch(query) {
	const trimmed = query.trim();
	return useQuery({
		queryKey: companiesKeys.nameSearch(trimmed),
		queryFn: () => searchCompaniesByName(trimmed),
		enabled: trimmed.length >= 2,
		staleTime: STALE_TIMES.STANDARD,
	});
}

// Company Detail Page queries

export function useCompanyDetail(companyId) {
	return useQuery({
		queryKey: companiesKeys.detail(companyId),
		queryFn: async () => {
			const { data, error } = await fetchCompanyDetail(companyId);
			if (error) throw error;
			return data;
		},
		staleTime: STALE_TIMES.STABLE,
		enabled: Boolean(companyId),
	});
}

export function useCompanyFinancialHistory(companyId) {
	return useQuery({
		queryKey: companiesKeys.financialHistory(companyId),
		queryFn: async () => {
			const { data, error } = await fetchCompanyFinancialHistory(companyId);
			if (error) throw error;
			return data;
		},
		staleTime: STALE_TIMES.STABLE,
		enabled: Boolean(companyId),
	});
}

export function useCompanyEvents(companyId) {
	return useQuery({
		queryKey: companiesKeys.events(companyId),
		queryFn: async () => {
			const { data, error } = await fetchCompanyEvents(companyId);
			if (error) throw error;
			return data;
		},
		staleTime: STALE_TIMES.STABLE,
		enabled: Boolean(companyId),
	});
}

export function useCompanyContacts(companyId) {
	return useQuery({
		queryKey: companiesKeys.contacts(companyId),
		queryFn: async () => {
			const { data, error } = await fetchCompanyContacts(companyId);
			if (error) throw error;
			return data;
		},
		staleTime: STALE_TIMES.STANDARD,
		enabled: Boolean(companyId),
	});
}

export function useCompanyDirectors(companyId) {
	return useQuery({
		queryKey: companiesKeys.directors(companyId),
		queryFn: async () => {
			const { data, error } = await fetchCompanyDirectors(companyId);
			if (error) throw error;
			return data;
		},
		staleTime: STALE_TIMES.STABLE,
		enabled: Boolean(companyId),
	});
}
